import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  Link as MuiLink,
  Alert,
  CircularProgress,
  Fade,
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { BRAND, MAIN_SITE_URL } from '../components/login/brand';
import {
  LoginHeader,
  LoginHero,
  HowItWorks,
  ProgramsGrid,
  Testimonials,
  Faq,
  FinalCta,
  LoginFooter,
} from '../components/login/LoginLanding';
import axios from 'axios';

// Track whether Google Sign-In has been initialized globally
let googleSignInInitialized = false;

const inputSx = {
  mb: 2,
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    background: '#fff',
    fontSize: '15px',
    '& fieldset': { borderColor: '#D9DCE1' },
    '&:hover fieldset': { borderColor: BRAND.copperLight },
    '&.Mui-focused fieldset': { borderColor: BRAND.copper, borderWidth: '1.5px' },
  },
  '& .MuiInputBase-input': { py: 1.6 },
};

const primaryButtonSx = {
  mb: 2,
  py: 1.5,
  borderRadius: '10px',
  background: BRAND.gradient,
  boxShadow: '0 6px 16px rgba(172,104,46,0.30)',
  color: '#fff',
  fontWeight: 700,
  fontSize: '15px',
  textTransform: 'none',
  '&:hover': {
    background: BRAND.gradientHover,
    boxShadow: '0 8px 20px rgba(172,104,46,0.38)',
  },
  '&.Mui-disabled': {
    background: BRAND.gradient,
    color: '#fff',
    opacity: 0.55,
  },
};

const secondaryLinkButtonSx = {
  color: BRAND.muted,
  textTransform: 'none',
  fontSize: '13px',
  '&:hover': {
    color: BRAND.copperDark,
    background: 'rgba(190,125,55,0.06)',
  },
};

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { login, loginWithToken } = useAuth();
  const [mode, setMode] = useState('magic-link');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);
  const cardRef = useRef(null);
  const emailInputRef = useRef(null);

  const handleGetStarted = () => {
    cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => emailInputRef.current?.focus({ preventScroll: true }), 450);
  };

  useEffect(() => {
    setMounted(true);

    // Check if coming from magic link verification
    const token = searchParams.get('token');
    if (token) {
      handleMagicLinkVerification(token);
    }
  }, [searchParams]);

  const handleMagicLinkVerification = async (token) => {
    try {
      setLoading(true);
      setMessage('Verifying your secure link...');
      
      const result = await loginWithToken(token);
      
      if (result.success) {
        navigate('/dashboard');
      } else {
        setError(result.message || 'Invalid or expired secure link. Please request a new one.');
        setMessage(null);
        setLoading(false);
      }
    } catch (err) {
      setError('Invalid or expired secure link. Please request a new one.');
      setMessage(null);
      setLoading(false);
    }
  };

  const handleMagicLinkSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    
    setLoading(true);
    setError(null);
    setMessage(null);

    const sendMagicLink = async (retryCount = 0) => {
      try {
        const response = await axios.post(
          `${import.meta.env.VITE_API_URL}/api/auth/magic-link`,
          { email },
          { timeout: 15000 }
        );

        if (response.data.success) {
          setMessage(t('auth.magicLinkSent'));
          
          if (response.data.magicLink) {
            console.log('Magic Link:', response.data.magicLink);
            setTimeout(() => {
              const token = response.data.magicLink.split('token=')[1];
              handleMagicLinkVerification(token);
            }, 2000);
          }
          return true;
        }
        return false;
      } catch (err) {
        if (retryCount === 0 && (err.code === 'ECONNABORTED' || err.code === 'ERR_NETWORK' || !err.response)) {
          return sendMagicLink(1);
        }
        throw err;
      }
    };

    try {
      await sendMagicLink();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send secure link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const result = await login(email, password);
    
    setLoading(false);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.message || 'Login failed');
    }
  };

  const handleGoogleSignIn = async (response) => {
    try {
      setLoading(true);
      setError(null);

      const credential = response.credential;
      const payload = JSON.parse(atob(credential.split('.')[1]));

      const authResponse = await axios.post(
        `${import.meta.env.VITE_API_URL}/api/auth/google`,
        {
          credential,
          googleId: payload.sub,
          email: payload.email,
          name: payload.name,
          picture: payload.picture
        }
      );

      if (authResponse.data.success) {
        const result = await loginWithToken(authResponse.data.token);
        if (result.success) {
          navigate('/dashboard');
        } else {
          setError('Google sign-in succeeded but session failed. Please try again.');
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const initializeGoogleSignIn = () => {
      if (cancelled || !window.google) return;

      if (!googleSignInInitialized) {
        window.google.accounts.id.initialize({
          client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
          callback: handleGoogleSignIn,
          use_fedcm_for_prompt: false,
        });
        googleSignInInitialized = true;
      }

      const btn = document.getElementById('google-signin-button');
      if (btn) {
        window.google.accounts.id.renderButton(btn, {
          theme: 'outline',
          size: 'large',
          width: Math.min(btn.offsetWidth || 400, 400),
          text: 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'center',
        });
        setGoogleReady(true);
      }
    };

    // Load script only if not already present
    const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    if (existing) {
      initializeGoogleSignIn();
    } else {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogleSignIn;
      document.body.appendChild(script);
    }

    return () => { cancelled = true; };
  }, []);

  const signInCard = (
    <Fade in={mounted} timeout={600}>
      <Box
        ref={cardRef}
        sx={{
          width: '100%',
          maxWidth: '440px',
          px: { xs: 3, sm: 3.5 },
          pt: { xs: 3, sm: 3.5 },
          pb: 3,
          borderRadius: '18px',
          background: '#fff',
          boxShadow: '0 24px 60px rgba(0,0,0,0.35), 0 4px 14px rgba(0,0,0,0.18)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: BRAND.gradient }} />

        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography
            component="h2"
            sx={{
              fontWeight: 800,
              color: BRAND.ink,
              fontSize: { xs: '1.4rem', sm: '1.55rem' },
              letterSpacing: '-0.01em',
              mb: 1,
            }}
          >
            {t('loginPage.cardTitle')}
          </Typography>
          <Typography sx={{ color: BRAND.body, fontSize: '14px', lineHeight: 1.55 }}>
            {t('loginPage.cardSubtitle')}
          </Typography>
        </Box>

        {/* Messages */}
        {message && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: '10px', '& .MuiAlert-icon': { alignItems: 'center' } }}>
            {message}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '10px', '& .MuiAlert-icon': { alignItems: 'center' } }}>
            {error}
          </Alert>
        )}

        {/* Login Form */}
        {mode === 'magic-link' ? (
          <Box component="form" onSubmit={handleMagicLinkSubmit}>
            <TextField
              fullWidth
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              autoFocus
              inputRef={emailInputRef}
              placeholder={t('auth.emailPlaceholder')}
              inputProps={{ 'aria-label': t('auth.email') }}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              sx={inputSx}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || !email}
              sx={primaryButtonSx}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : t('auth.sendMagicLink')}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="text"
                size="small"
                onClick={() => setMode('password')}
                startIcon={<LockIcon sx={{ fontSize: 16 }} />}
                sx={secondaryLinkButtonSx}
              >
                {t('auth.usePasswordInstead')}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box component="form" onSubmit={handlePasswordSubmit}>
            <TextField
              fullWidth
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              autoFocus
              inputRef={emailInputRef}
              placeholder={t('auth.emailPlaceholder')}
              inputProps={{ 'aria-label': t('auth.email') }}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              sx={inputSx}
            />

            <TextField
              fullWidth
              name="password"
              type="password"
              id="password"
              autoComplete="current-password"
              placeholder={t('auth.passwordPlaceholder')}
              inputProps={{ 'aria-label': t('auth.password') }}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              sx={inputSx}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={primaryButtonSx}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : t('auth.signIn')}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="text"
                size="small"
                onClick={() => setMode('magic-link')}
                startIcon={<EmailIcon sx={{ fontSize: 16 }} />}
                sx={secondaryLinkButtonSx}
              >
                {t('auth.useMagicLinkInstead')}
              </Button>
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 2, color: '#9CA3AF', '&::before, &::after': { borderColor: BRAND.border } }}>
          <Typography sx={{ color: '#9CA3AF', px: 1, fontSize: '13px' }}>
            {t('auth.or')}
          </Typography>
        </Divider>

        {/* Google Sign-In Button */}
        <Box sx={{ mb: 2.5, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div id="google-signin-button" style={{ width: '100%', display: 'flex', justifyContent: 'center' }}></div>
          {!googleReady && (
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                if (window.google) {
                  window.google.accounts.id.prompt();
                }
              }}
              startIcon={
                <img
                  src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                  alt="Google"
                  style={{ width: 18, height: 18 }}
                />
              }
              sx={{
                height: 46,
                borderRadius: '10px',
                borderColor: '#D9DCE1',
                color: BRAND.ink,
                fontSize: '15px',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': { borderColor: BRAND.copperLight, background: '#FFFBF6' },
              }}
            >
              {t('auth.signInWithGoogle')}
            </Button>
          )}
        </Box>

        {/* Terms */}
        <Typography sx={{ textAlign: 'center', color: BRAND.muted, fontSize: '12px', mb: 2 }}>
          {t('auth.termsPrefix')}{' '}
          <MuiLink
            href={`${MAIN_SITE_URL}/terms`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: BRAND.copperDark, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            {t('auth.termsOfService')}
          </MuiLink>{' '}
          {t('auth.and')}{' '}
          <MuiLink
            href={`${MAIN_SITE_URL}/privacy`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: BRAND.copperDark, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            {t('auth.privacyPolicy')}
          </MuiLink>
        </Typography>

        <Divider sx={{ borderColor: '#F0F1F3', mb: 2 }} />

        {/* Sign Up Link */}
        <Typography sx={{ textAlign: 'center', color: BRAND.body, fontSize: '14px' }}>
          {t('auth.dontHaveAccount')}{' '}
          <MuiLink
            component={Link}
            to="/register"
            sx={{
              fontWeight: 700,
              textDecoration: 'none',
              color: BRAND.copperDark,
              '&:hover': { color: BRAND.copper, textDecoration: 'underline' },
            }}
          >
            {t('auth.signUp')}
          </MuiLink>
        </Typography>
      </Box>
    </Fade>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        background: '#fff',
        fontFamily: 'Inter, Roboto, Helvetica, Arial, sans-serif',
        // Global preflight is disabled, so opt this page into border-box sizing.
        '&, & *, & *::before, & *::after': { boxSizing: 'border-box' },
        '& .MuiInputBase-input': { boxSizing: 'content-box' },
      }}
    >
      <LoginHeader />
      <Box component="main">
        <LoginHero card={signInCard} />
        <HowItWorks />
        <ProgramsGrid />
        <Testimonials />
        <Faq />
        <FinalCta onGetStarted={handleGetStarted} />
      </Box>
      <LoginFooter />
    </Box>
  );
};

export default Login;
